import { useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { useItemVariantAxesFor, useItems, useVendors, type Item } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { purchaseDeskKey, useSupplierDesk } from '@/api/purchaseDesk';
import {
  createPurchaseOrder,
  purchaseOrdersKey,
  updatePurchaseOrder,
  type PurchaseOrder,
} from '@/api/purchaseOrders';
import { DatePicker } from '@/components/DatePicker';
import { DocumentHeading, DocumentLines, type DocumentColumn } from '@/components/DocumentSheet';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass, selectClass, textAreaClass } from '@/components/Field';
import { IconAction } from '@/components/IconAction';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { useDebounced } from '@/components/useDebounced';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import {
  countsFromOrder,
  orderFromCounts,
  orderProblems,
  orderTotals,
  toOrderRequest,
} from '@core/purchasing/order';
import type { Session } from '@core/session/types';
import {
  lineCount,
  maxPerSize,
  receiveLine,
  receiveProblems,
  sizeChoices,
  splitAxes,
  updateReceiveLine,
  type ReceiveDraft,
  type ReceiveLine,
} from '@core/units/receive';
import { Thumb } from '../sales/SaleCounter';
import { OrderSupplierFirst } from './OrderSupplierFirst';

const rupees = (value: number) => `₹${formatAmount(value)}`;

/**
 * Purchases › New purchase order (canvas "Purchase order flow", 2026-10-25) — the template's entry
 * form: the supplier and the order's details side by side, then what is being ordered as one row per
 * design and colour with a box for each size, one rate and the amount, and notes beside the totals.
 * The grid is the phone Receive's (`units/receive.ts`) turned into order lines by `orderFromCounts`,
 * so an order and the delivery against it count a design the same way.
 *
 * <p>
 * The number is issued when the order is saved, never typed. Rates are before tax: GST comes on the
 * supplier's bill when the stock arrives.
 * </p>
 *
 * <p>
 * <strong>Change is the same page</strong>, opened on the order while nothing has come against it
 * (`canEdit`): its lines are put back on the grid by `countsFromOrder` once every design's sizes
 * have loaded, and saving sends the whole order again.
 * </p>
 */
export function OrderComposer({
  session,
  vendorId,
  editing,
  trail = 'Purchases',
  onClose,
  onSaved,
}: {
  session: Session;
  /** The order being changed; absent places a new one. */
  editing?: PurchaseOrder | undefined;
  /** The supplier the desk had open, chosen already; null asks. */
  vendorId: number | null;
  /** Where Back goes, as the band's trail names it. */
  trail?: string;
  onClose: () => void;
  /** `print` when it was saved with Save and print. */
  onSaved: (order: PurchaseOrder, print: boolean) => void;
}) {
  const branchId = session.branch?.id;
  const today = dayjs().format('YYYY-MM-DD');
  const [counted, setCounted] = useState<ReceiveDraft>(() => ({
    receivedOn: today,
    supplierVendorId: editing?.supplierVendorId ?? vendorId,
    note: '',
    lines: [],
  }));
  const [orderDate, setOrderDate] = useState(editing?.orderDate ?? today);
  const [expectedOn, setExpectedOn] = useState(editing?.expectedOn ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [seeded, setSeeded] = useState(editing === undefined);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplier, setSupplier] = useState<PickerOption | null>(null);
  const [designSearch, setDesignSearch] = useState('');
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const suppliers = useVendors(branchId, {
    capability: 'Supplier',
    search: supplierSearch,
    pageSize: 10,
  });
  const known = useVendors(branchId, { capability: 'Supplier', pageSize: 200 });
  const chosen = (known.data?.items ?? []).find((vendor) => vendor.id === counted.supplierVendorId);
  const chosenOption =
    supplier ??
    (chosen
      ? { id: String(chosen.id), primary: chosen.name, secondary: chosen.mobile }
      : editing && counted.supplierVendorId === editing.supplierVendorId
        ? { id: String(editing.supplierVendorId), primary: editing.supplierName }
        : null);
  const desk = useSupplierDesk(counted.supplierVendorId);
  const owed = desk.data?.supplier.owed ?? null;

  const searched = useDebounced(designSearch, 250);
  const designs = useItems(branchId, { search: searched || undefined, pageSize: 12 });
  const catalogue = useItems(branchId, { includeInactive: true, pageSize: 200 });
  const itemsById = useMemo(
    () => new Map((catalogue.data?.items ?? []).map((item) => [item.id, item])),
    [catalogue.data],
  );

  const itemIds = counted.lines
    .map((line) => line.itemId)
    .filter((id): id is number => id !== null);
  const orderedIds = editing?.lines.map((line) => line.itemId) ?? [];
  const { axes } = useItemVariantAxesFor([...itemIds, ...orderedIds]);

  // The order goes on the grid once, when every design on it has said its sizes.
  if (!seeded && editing && orderedIds.every((id) => axes[id] !== undefined)) {
    setSeeded(true);
    setCounted((was) => ({ ...countsFromOrder(editing, axes), receivedOn: was.receivedOn }));
  }
  const name = (itemId: number) => itemsById.get(itemId)?.name ?? 'this design';

  const order = orderFromCounts(
    { supplierVendorId: counted.supplierVendorId, orderDate, expectedOn, notes },
    counted,
    axes,
  );
  const totals = orderTotals(order);
  const problems = !seeded
    ? ['Reading the order…']
    : counted.lines.length === 0
      ? ['Add a design to order.']
      : [
          ...(counted.supplierVendorId === null ? ['Choose the supplier.'] : []),
          ...receiveProblems(counted, axes, name).filter((p) => !p.startsWith('Choose the design')),
          ...counted.lines
            .filter((line) => line.purchaseCost !== null && line.purchaseCost < 0)
            .map(() => 'A rate cannot be negative.'),
        ];
  const serverProblems = problems.length === 0 ? orderProblems(order, axes) : [];
  const allProblems = [...problems, ...serverProblems];

  const save = useApiMutation<PurchaseOrder, boolean>({
    mutationFn: () =>
      editing
        ? updatePurchaseOrder(editing.id, toOrderRequest(order))
        : createPurchaseOrder(toOrderRequest(order)),
    success: (saved) =>
      editing
        ? `${saved.documentNumber} saved.`
        : `${saved.documentNumber} placed with ${saved.supplierName}.`,
    invalidates: [purchaseOrdersKey, purchaseDeskKey],
    onSuccess: (saved, print) => onSaved(saved, print),
  });
  const busy = save.isPending;
  const ready = !busy && allProblems.length === 0;
  const submit = (print: boolean) => void save.mutateAsync(print).catch(() => undefined);

  const pick = (item: Item) => {
    setCounted((was) => ({
      ...was,
      lines: [...was.lines, { ...receiveLine(item.id), purchaseCost: null }],
    }));
    setDesignSearch('');
  };
  const set = (line: ReceiveLine, change: Parameters<typeof updateReceiveLine>[2]) =>
    setCounted((was) => updateReceiveLine(was, line.key, change));
  const designCount = new Set(itemIds).size;

  const columns: DocumentColumn<ReceiveLine>[] = [
    {
      key: 'design',
      title: 'Design',
      render: (line) => {
        const item = line.itemId === null ? undefined : itemsById.get(line.itemId);

        return (
          <div className="flex min-w-44 items-center gap-2.5">
            <Thumb url={item?.primaryImageUrl ?? null} />
            <div className="min-w-0">
              <p className="mb-0 font-semibold text-gray-900">{item?.name ?? 'Design'}</p>
              <p className="text-default font-mono-nums mb-0 text-[length:var(--scale-list-meta)]">
                {item?.code}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'look',
      title: 'Colour',
      render: (line) => {
        const looks =
          line.itemId !== null && axes[line.itemId] ? splitAxes(axes[line.itemId]!).looks : [];

        return looks.length === 0 ? (
          <span className="text-default">—</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {looks.map((axis) => (
              <select
                key={axis.attributeId}
                aria-label={axis.attributeName}
                value={line.look[axis.attributeId] ?? ''}
                disabled={busy}
                onChange={(event) =>
                  set(line, {
                    look: { ...line.look, [axis.attributeId]: Number(event.target.value) },
                  })
                }
                className={`${selectClass} min-w-32`}
              >
                <option value="">{axis.attributeName}</option>
                {axis.values.map((value) => (
                  <option key={value.valueId} value={value.valueId}>
                    {value.name}
                  </option>
                ))}
              </select>
            ))}
          </div>
        );
      },
    },
    {
      key: 'sizes',
      title: 'How many of each size',
      render: (line) => {
        const item = line.itemId === null ? undefined : itemsById.get(line.itemId);
        const sizes =
          line.itemId !== null && axes[line.itemId] ? sizeChoices(axes[line.itemId]!) : [];

        return (
          <ul className="m-0 flex list-none flex-wrap gap-1.5 ps-0">
            {sizes.map((size) => (
              <li key={size.key} className="list-none">
                <label className="flex flex-col items-center gap-0.5">
                  <span className="text-default text-[11px]">
                    {size.label === 'One size' ? 'How many' : size.label}
                  </span>
                  <input
                    type="number"
                    aria-label={`${item?.name ?? 'Design'} size ${size.label}`}
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    max={maxPerSize}
                    value={line.counts[size.key] ?? ''}
                    placeholder="0"
                    disabled={busy}
                    onChange={(event) =>
                      set(line, {
                        counts: {
                          ...line.counts,
                          [size.key]: Math.min(
                            maxPerSize,
                            Math.max(0, Number(event.target.value) || 0),
                          ),
                        },
                      })
                    }
                    className={`${controlClass} w-14 px-1 text-center`}
                  />
                </label>
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      key: 'garments',
      title: 'Garments',
      numeric: true,
      render: (line) => <span className="font-semibold">{lineCount(line)}</span>,
    },
    {
      key: 'rate',
      title: 'Rate (₹)',
      numeric: true,
      render: (line) => (
        <div>
          <input
            type="number"
            aria-label={`Rate for ${line.itemId === null ? 'this design' : name(line.itemId)}`}
            onWheel={(event) => event.currentTarget.blur()}
            min={0}
            step="0.01"
            value={line.purchaseCost ?? ''}
            disabled={busy}
            onChange={(event) =>
              set(line, {
                purchaseCost: event.target.value === '' ? null : Number(event.target.value),
              })
            }
            className={`${controlClass} w-28 text-right`}
          />
          <p className="text-default mt-0.5 mb-0 text-[11px]">per garment</p>
        </div>
      ),
    },
    {
      key: 'amount',
      title: 'Amount',
      numeric: true,
      render: (line) => (
        <span className="font-semibold">
          {line.purchaseCost === null ? '—' : rupees(lineCount(line) * line.purchaseCost)}
        </span>
      ),
    },
    {
      key: 'remove',
      title: '',
      render: (line) => (
        <IconAction
          icon="icon icon-trash-2"
          label={`Remove ${line.itemId === null ? 'this design' : name(line.itemId)}`}
          onClick={() =>
            setCounted((was) => ({ ...was, lines: was.lines.filter((l) => l.key !== line.key) }))
          }
        />
      ),
    },
  ];

  // A new order asks who it is from before anything else (Supplier A); the picker below changes it.
  if (!editing && counted.supplierVendorId === null) {
    return (
      <OrderSupplierFirst
        session={session}
        trail={trail}
        onClose={onClose}
        onPick={(id) => setCounted((was) => ({ ...was, supplierVendorId: id }))}
      />
    );
  }

  return (
    <>
      <DocumentHeading
        trail={trail}
        current={editing ? 'Change' : 'New'}
        title={editing ? `Change ${editing.documentNumber}` : 'New purchase order'}
        subtitle={
          editing
            ? 'Nothing has come against it yet, so every line can still change.'
            : 'Who it is from, when it should come, and how many of each size.'
        }
        onBack={onClose}
        actions={
          <Button icon="icon icon-arrow-left" shortcut="back" disabled={busy} onClick={onClose}>
            Back
          </Button>
        }
      />

      <div ref={fields} {...enterKeyScope}>
        <Card>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <fieldset className="m-0 min-w-0 border-0 p-0">
              <SectionTitle icon="icon icon-truck" hue="teal">
                Supplier
              </SectionTitle>
              <div className="flex flex-col gap-3">
                <Field label="For branch">
                  <input
                    value={session.branch?.name ?? ''}
                    readOnly
                    tabIndex={-1}
                    className={`${controlClass} bg-light`}
                  />
                </Field>
                {/* Not a Field: a label around the picker would hand its clicks to the first button. */}
                <div>
                  <span className="mb-1 block text-sm font-semibold text-gray-900">
                    Supplier <span className="text-danger ms-0.5">*</span>
                  </span>
                  <SearchPicker
                    value={chosenOption}
                    options={(suppliers.data?.items ?? []).map((vendor) => ({
                      id: String(vendor.id),
                      primary: vendor.name,
                      secondary: vendor.mobile,
                    }))}
                    total={suppliers.data?.total ?? 0}
                    loading={suppliers.isLoading}
                    disabled={busy}
                    placeholder="Search suppliers"
                    onSearch={setSupplierSearch}
                    onSelect={(option) => {
                      setSupplier(option);
                      setCounted((was) => ({
                        ...was,
                        supplierVendorId: option === null ? null : Number(option.id),
                      }));
                    }}
                  />
                </div>
                {chosen && (
                  <p className="text-default mb-0 flex flex-wrap gap-x-3 gap-y-0.5 text-[length:var(--scale-list-meta)]">
                    <span className="font-mono-nums">{chosen.mobile}</span>
                    {(chosen.city || chosen.stateName) && (
                      <span>{[chosen.city, chosen.stateName].filter(Boolean).join(', ')}</span>
                    )}
                    {chosen.gstin && <span>GSTIN {chosen.gstin}</span>}
                    {owed !== null && owed > 0 && (
                      <span className="font-semibold text-[color:var(--color-warning)]">
                        {rupees(owed)} still to pay
                      </span>
                    )}
                  </p>
                )}
              </div>
            </fieldset>

            <fieldset className="m-0 min-w-0 border-0 p-0">
              <SectionTitle icon="icon icon-file-text" hue="primary">
                Order details
              </SectionTitle>
              <div className="flex flex-col gap-3">
                <Field label="PO number">
                  <input
                    value={editing?.documentNumber ?? 'Issued when you save'}
                    readOnly
                    tabIndex={-1}
                    className={`${controlClass} bg-light text-default`}
                  />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Order date" required>
                    <DatePicker
                      className="w-full"
                      value={toDayjs(orderDate) ?? null}
                      allowClear={false}
                      disabled={busy}
                      onChange={(value) => setOrderDate(toIsoDate(value) ?? orderDate)}
                    />
                  </Field>
                  <Field label="Expected delivery" hint="When the supplier said it would come.">
                    <DatePicker
                      className="w-full"
                      value={toDayjs(expectedOn) ?? null}
                      disabled={busy}
                      onChange={(value) => setExpectedOn(toIsoDate(value) ?? '')}
                    />
                  </Field>
                </div>
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                  Rates are before tax. GST comes on the supplier&apos;s bill when the stock
                  arrives.
                </p>
              </div>
            </fieldset>
          </div>

          <div className="border-border-color mt-6 border-t pt-5">
            <SectionTitle
              icon="icon icon-shirt"
              hue="purple"
              trailing={
                <span className="text-default text-[length:var(--scale-list-meta)]">
                  One row per design and colour · a count for each size
                </span>
              }
            >
              What you are ordering
            </SectionTitle>

            {counted.lines.length > 0 && (
              <DocumentLines columns={columns} rows={counted.lines} rowKey={(line) => line.key} />
            )}

            <label className="relative mt-1 flex items-center">
              <i
                className="icon icon-search text-default pointer-events-none absolute start-3"
                aria-hidden="true"
              />
              <input
                type="search"
                aria-label="Add a design"
                placeholder="Add a design — its name or short code"
                value={designSearch}
                disabled={busy}
                onChange={(event) => setDesignSearch(event.target.value)}
                className={`${controlClass} ps-9`}
              />
            </label>
            {designSearch.trim() !== '' && (
              <ul className="mt-3 grid list-none grid-cols-2 gap-2 ps-0 md:grid-cols-4 xl:grid-cols-6">
                {(designs.data?.items ?? []).map((item) => (
                  <li key={item.id} className="list-none">
                    <button
                      type="button"
                      onClick={() => pick(item)}
                      className="border-border-color hover:border-primary flex w-full cursor-pointer flex-col items-start gap-2 rounded-md border p-2 text-start"
                    >
                      <Thumb url={item.primaryImageUrl} size="h-24 w-full" />
                      <span className="text-xs font-semibold text-gray-900">{item.name}</span>
                      <span className="text-default font-mono-nums text-[11px]">{item.code}</span>
                    </button>
                  </li>
                ))}
                {designs.data?.items.length === 0 && (
                  <li className="text-default col-span-full list-none text-[length:var(--scale-list-meta)]">
                    No design matches “{designSearch.trim()}”.
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className="border-border-color mt-6 grid grid-cols-1 gap-6 border-t pt-5 lg:grid-cols-2">
            <Field label="Notes for the supplier">
              <textarea
                rows={5}
                value={notes}
                placeholder="Lining, buttons, packing"
                disabled={busy}
                onChange={(event) => setNotes(event.target.value)}
                className={textAreaClass}
              />
            </Field>
            <div className="bg-light flex flex-col gap-2 self-start rounded-md p-4 text-[length:var(--scale-font-size)]">
              <p className="mb-0 flex justify-between">
                <span className="text-default">Designs</span>
                <span className="font-mono-nums font-semibold text-gray-900">{designCount}</span>
              </p>
              <p className="mb-0 flex justify-between">
                <span className="text-default">Garments</span>
                <span className="font-mono-nums font-semibold text-gray-900">{totals.units}</span>
              </p>
              <p className="border-border-color mb-0 flex items-baseline justify-between border-t pt-2">
                <span className="font-bold text-gray-900">Value before tax</span>
                <span className="text-primary font-mono-nums text-xl font-bold">
                  {totals.value === null ? '—' : rupees(totals.value)}
                </span>
              </p>
            </div>
          </div>

          {allProblems.length > 0 && counted.lines.length > 0 && (
            <div className="mt-4">
              <Notice tone="warning" title="Not ready yet">
                <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
                  {allProblems.slice(0, 6).map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Notice>
            </div>
          )}

          <div className="border-border-color mt-6 flex flex-wrap justify-end gap-2 border-t pt-4">
            <Button disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button icon="icon icon-printer" disabled={!ready} onClick={() => submit(true)}>
              Save and print
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              disabled={!ready}
              onClick={() => submit(false)}
            >
              {busy ? 'Saving…' : 'Save order'}
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}
